import RectangleLibrary from "../../libraries/RectangleLibrary";
import layerLibrary from "../../canvas/LayerLibrary";
import layoutEngine from "../../libraries/layoutEngine";

function getParamMap(parameters = []) {
  const map = {};
  for (const p of parameters) {
    map[p.originalName || p.name] = p.value;
  }
  return map;
}

function toNumber(value, fallback = 0) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function evalExpr(expr, paramMap = {}) {
  if (typeof expr === "number") return expr;
  if (typeof expr !== "string") return 0;

  const trimmed = expr.trim();

  if (trimmed in paramMap) {
    const val = paramMap[trimmed];
    if (typeof val === "number") return val;
    return toNumber(val, 0);
  }

  const scope = {};
  for (const [k, v] of Object.entries(paramMap)) {
    if (typeof v === "number") scope[k] = v;
    else if (typeof v !== "object") scope[k] = toNumber(v, 0);
  }

  try {
    const fn = new Function(...Object.keys(scope), `return (${trimmed});`);
    return toNumber(fn(...Object.values(scope)), 0);
  } catch {
    return 0;
  }
}

function getLayerInfo(layerParamName, paramMap) {
  const layerValue = paramMap[layerParamName] || { layer: "M0", datatype: "drawing" };

  const layerObj = layerLibrary.getAllLayers().find(
    (l) =>
      l.layer_name === layerValue.layer &&
      l.datatype_name === layerValue.datatype
  );

  return {
    layerObj,
    layerId: `${layerObj?.layer_number ?? 1}:${layerObj?.datatype_number ?? 0}`,
  };
}

function generateBlanketRects(definition, paramMap) {
  const rectGroups = {};

  for (const item of definition.design || []) {
    const name = item.name;
    const repX = Math.max(1, Math.round(evalExpr(item.repetition?.x, paramMap)));
    const repY = Math.max(1, Math.round(evalExpr(item.repetition?.y, paramMap)));
    const width = evalExpr(item.width, paramMap);
    const height = evalExpr(item.height, paramMap);
    const spacingX = evalExpr(item.spacing?.x, paramMap);
    const spacingY = evalExpr(item.spacing?.y, paramMap);

    const { layerObj, layerId } = getLayerInfo(item.layer, paramMap);

    const rects = [];
    for (let y = 0; y < repY; y++) {
      for (let x = 0; x < repX; x++) {
        rects.push({
          // Pattern geometry is evaluated in one canonical Cartesian space:
          // bottom-left origin, +X right and +Y up.
          x: x * (width + spacingX),
          y: y * (height + spacingY),
          width,
          height,
          layerObj,
          layerId,
        });
      }
    }

    rectGroups[name] = rects;
  }

  return rectGroups;
}

function getBounds(rects = []) {
  if (!rects.length) return { minX: 0, minY: 0, maxX: 0, maxY: 0, cx: 0, cy: 0 };

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }

  return {
    minX, minY, maxX, maxY,
    cx: (minX + maxX) / 2,
    cy: (minY + maxY) / 2,
  };
}

export function getPointerPoint(pointer, bounds) {
  switch (String(pointer || "").toLowerCase()) {
    case "center": return { x: bounds.cx, y: bounds.cy };
    case "um": return { x: bounds.cx, y: bounds.maxY };
    case "ur": return { x: bounds.maxX, y: bounds.maxY };
    case "ul": return { x: bounds.minX, y: bounds.maxY };
    case "lem": return { x: bounds.minX, y: bounds.cy };
    case "rm": return { x: bounds.maxX, y: bounds.cy };
    case "ll": return { x: bounds.minX, y: bounds.minY };
    case "lm": return { x: bounds.cx, y: bounds.minY };
    case "lr": return { x: bounds.maxX, y: bounds.minY };
    case "ref": return { x: bounds.minX, y: bounds.minY };
    default: return { x: bounds.minX, y: bounds.minY };
  }
}


function convertToCanvasBottomLeft(rects = []) {
  if (!rects.length) return rects;

  const bounds = getBounds(rects);

  // Canvas rectangles use a top-left Y coordinate and grow downward. Convert
  // exactly once after all logical (+Y-up) placement rules have been applied.
  // The complete PCell keeps its local bottom-left fixed at (0, 0).
  return rects.map((r) => ({
    ...r,
    x: r.x - bounds.minX,
    y: -(r.y + r.height - bounds.minY),
  }));
}

function alignPlacement(rectGroups, placement = []) {
  const placedGroups = Object.fromEntries(
    Object.entries(rectGroups).map(([name, rects]) => [
      name,
      rects.map((rect) => ({ ...rect })),
    ])
  );

  for (const rule of placement) {
    const specs = Object.values(rule || {}).filter(
      (spec) => spec?.name && placedGroups[spec.name]
    );

    if (specs.length <= 1) continue;

    const baseSpec = specs[0];
    const baseBounds = getBounds(placedGroups[baseSpec.name]);
    const basePoint = getPointerPoint(baseSpec.pointer, baseBounds);

    for (let i = 1; i < specs.length; i++) {
      const spec = specs[i];
      const group = placedGroups[spec.name];
      const bounds = getBounds(group);
      const point = getPointerPoint(spec.pointer, bounds);
      const dx = basePoint.x - point.x;
      const dy = basePoint.y - point.y;

      for (const rect of group) {
        rect.x += dx;
        rect.y += dy;
      }
    }
  }

  return Object.values(placedGroups).flat();
}

export function buildPCellRectangles(definitionSnapshot, parameters = []) {
  if (!definitionSnapshot || !Array.isArray(definitionSnapshot.design)) {
    throw new Error("Invalid or missing definitionSnapshot");
  }

  const paramMap = getParamMap(parameters);
  const rectGroups = generateBlanketRects(definitionSnapshot, paramMap);
  const alignedRects = alignPlacement(
    rectGroups,
    definitionSnapshot.placement || []
  );

  return convertToCanvasBottomLeft(alignedRects);
}

export function rebuildPCellFromDefinition(cellName, definitionSnapshot, parameters = []) {
  const finalRects = buildPCellRectangles(definitionSnapshot, parameters);

  let cell = layoutEngine.getCellByName(cellName);
  if (!cell) {
    layoutEngine.addCell(cellName);
    cell = layoutEngine.getCellByName(cellName);
  }
  if (cell) cell.elements = [];

  for (const rect of finalRects) {
    RectangleLibrary.addRectangle(cellName, rect.layerId, rect.x, rect.y, rect.width, rect.height, {
      id: crypto.randomUUID(),
      color: rect.layerObj?.color || "green",
      fill: rect.layerObj?.color || "rgba(0,255,0,0.4)",
      stroke: rect.layerObj?.color || "green",
      layerNumber: rect.layerObj?.layer_number,
      datatypeNumber: rect.layerObj?.datatype_number,
      layerName: rect.layerObj?.layer_name,
    });
  }

  return cellName;
}
